import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaTimes, FaPlus, FaSearch } from 'react-icons/fa';
import './AddCourtDateModal.css';

const HEARING_TYPES = [
  'Admit/Deny Hearing',
  'Advisement',
  'Answer Hearing',
  'Appeal Hearing',
  'Appearance',
  'Arraignment',
  'Bail Modification Hearing',
  'Bond Hearing',
  'Change of Plea',
  'Compliance Hearing',
  'Conference - Scheduling',
  'Conference - PreTrial',
  'Consent Decree',
  'Contempt Hearing',
  'Court Exonerated',
  'Court Trial',
  'Court Update',
  'Courtesy Hearing',
  'Dismissal Hearing',
  'Disposition Hearing',
  'Evidentiary Hearing',
  'Ex parte hearing',
  'Expungement Request Hearing',
  'Extradition Hearing',
  'Final Pretrial Conference',
  'First Appearance',
  'Hearing',
  'Hearing on Motion',
  'Initial Appearance',
  'Initial Appearance Lewis and Clark',
  'Jail Hearing',
  'Judge Trial',
  'Jury Confirmation',
  'Jury Draw',
  'Jury Instruction Hearing',
  'Jury Trial',
  'Jury Trial Conference',
  'Mandate hearing',
  'Mediation',
  'Mental competency hearing',
  'Mental competency restoration hearing',
  'Mitigation',
  'Motion for continuance',
  'Motion for discovery',
  'Motion for new trial hearing',
  'Motion to adjudicate hearing',
  'Motion to Dismiss',
  'Motion to suppress evidence Hearing',
  'Motion to vacate',
  'Motions Hearing',
  'Non-Jury Trial',
  'Non-trial setting',
  'NOT A HEARING',
  'Omnibus Hearing',
  'Order to Show Cause',
  'Parenting Plan Hearing',
  'Petition To Revoke',
  'Petition to Revoke - OR',
  'Petition to Revoke - Sentence',
  'Plea Hearing',
  'Post Sentence Hearing',
  'Prelim Appearance',
  'Preliminary Hearing',
  'Pre-sentence investigation hearing',
  'Pretrial Conference',
  'Pretrial Hearing',
  'Pre-Trial Status Conference',
  'Probation Violation Hearing',
  'Representation Hearing',
  'Restitution Hearing',
  'Restoration of sanity hearing',
  'Review Hearing',
  'Scheduling Hearing',
  'Sentencing',
  'Set for judgment and sentence',
  'Setting Hearing',
  'Show Cause Hearing',
  'Status',
  'Status Hearing',
  'Stipulation',
  'Summons',
  'Transfer Hearing',
  'Waiver of extradition',
  'Waiver of hearing',
  'Waiver of speedy trial',
  'Warrant Hearing',
  'Writ of habeas corpus hearing'
];

const AddCourtDateModal = ({ isOpen, onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [bondSearch, setBondSearch] = useState('');
  const [bondResults, setBondResults] = useState([]);
  const [showBondDropdown, setShowBondDropdown] = useState(false);
  const [selectedBond, setSelectedBond] = useState(null);

  const [courts, setCourts] = useState([]);
  const [selectedCourt, setSelectedCourt] = useState(null);
  const [judges, setJudges] = useState([]);
  const [selectedJudge, setSelectedJudge] = useState('');
  const [showAddJudge, setShowAddJudge] = useState(false);
  const [newJudgeName, setNewJudgeName] = useState('');

  const [formData, setFormData] = useState({
    courtDate: '',
    courtTime: '09:00',
    courtRoom: '',
    courtJurisdiction: '',
    hearingType: 'Initial Appearance',
    caseNumber: '',
    comments: '',
    setReminders: false,
    appearanceType: 'In Person'
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      fetchCourts();
      resetForm();
    }
  }, [isOpen]);

  useEffect(() => {
    // Debounced bond search
    if (bondSearch.length >= 2) {
      const timeout = setTimeout(() => {
        searchBonds(bondSearch);
      }, 300);
      return () => clearTimeout(timeout);
    } else {
      setBondResults([]);
      setShowBondDropdown(false);
    }
  }, [bondSearch]);

  useEffect(() => {
    // Fetch judges when court is selected
    if (selectedCourt) {
      fetchJudges(selectedCourt.id);
      setFormData(prev => ({
        ...prev,
        courtJurisdiction: selectedCourt.jurisdiction || ''
      }));
    }
  }, [selectedCourt]);

  const resetForm = () => {
    setBondSearch('');
    setSelectedBond(null);
    setSelectedCourt(null);
    setSelectedJudge('');
    setShowAddJudge(false);
    setNewJudgeName('');
    setFormData({
      courtDate: '',
      courtTime: '09:00',
      courtRoom: '',
      courtJurisdiction: '',
      hearingType: 'Initial Appearance',
      caseNumber: '',
      comments: '',
      setReminders: false,
      appearanceType: 'In Person'
    });
    setErrors({});
  };

  const fetchCourts = async () => {
    try {
      const response = await axios.get('/courts');
      if (response.data.success) {
        setCourts(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching courts:', error);
    }
  };

  const fetchJudges = async (courtId) => {
    try {
      const response = await axios.get(`/courts/${courtId}/judges`);
      if (response.data.success) {
        setJudges(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching judges:', error);
      setJudges([]);
    }
  };

  const searchBonds = async (query) => {
    try {
      const response = await axios.get('/bonds', {
        params: {
          search: query,
          limit: 10
        }
      });
      if (response.data.success) {
        setBondResults(response.data.data);
        setShowBondDropdown(true);
      }
    } catch (error) {
      console.error('Error searching bonds:', error);
      setBondResults([]);
    }
  };

  const handleSelectBond = (bond) => {
    setSelectedBond(bond);
    setBondSearch(`${bond.bond_number} - ${bond.defendant_name}`);
    setShowBondDropdown(false);

    // Auto-fill case number if available
    if (bond.case_number) {
      setFormData(prev => ({ ...prev, caseNumber: bond.case_number }));
    }

    // Auto-select court if available
    if (bond.court_id) {
      const court = courts.find(c => c.id === bond.court_id);
      if (court) {
        setSelectedCourt(court);
      }
    }
  };

  const handleCourtChange = (e) => {
    const courtId = e.target.value;
    const court = courts.find(c => c.id === courtId);
    setSelectedCourt(court);
    setSelectedJudge('');
  };

  const handleJudgeChange = (e) => {
    const value = e.target.value;
    if (value === '__add_new__') {
      setShowAddJudge(true);
      setSelectedJudge('');
    } else {
      setSelectedJudge(value);
      setShowAddJudge(false);
    }
  };

  const handleAddJudge = async () => {
    if (!newJudgeName.trim() || !selectedCourt) {
      return;
    }

    try {
      const response = await axios.post(`/courts/${selectedCourt.id}/judges`, {
        name: newJudgeName.trim(),
        title: 'Judge'
      });

      if (response.data.success) {
        const newJudge = response.data.data;
        setJudges([...judges, newJudge]);
        setSelectedJudge(newJudge.id);
        setShowAddJudge(false);
        setNewJudgeName('');
      }
    } catch (error) {
      console.error('Error adding judge:', error);
      alert('Failed to add judge. Please try again.');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!selectedBond) {
      newErrors.bond = 'Please select a bond';
    }
    if (!selectedCourt) {
      newErrors.court = 'Please select a court';
    }
    if (!formData.courtDate) {
      newErrors.courtDate = 'Court date is required';
    }
    if (!formData.hearingType) {
      newErrors.hearingType = 'Hearing type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const payload = {
        bondId: selectedBond.id,
        defendantId: selectedBond.defendant_id,
        courtId: selectedCourt.id,
        scheduledDate: formData.courtDate,
        scheduledTime: formData.courtTime,
        hearingType: formData.hearingType,
        caseNumber: formData.caseNumber || selectedBond.case_number,
        judgeName: selectedJudge ? judges.find(j => j.id === selectedJudge)?.name : null,
        courtroom: formData.courtRoom,
        notes: formData.comments,
        appearanceType: formData.appearanceType
      };

      const response = await axios.post('/court-dates', payload);

      if (response.data.success) {
        onSave(response.data.data);
        onClose();
        resetForm();
      }
    } catch (error) {
      console.error('Error creating court date:', error);
      alert('Failed to create court date. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content court-date-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add Court Date</h2>
          <button className="modal-close-btn" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="modal-body">
          {/* Bond Search */}
          <div className="form-group">
            <label>Search Bond (Power Number, Case Number, or Defendant) *</label>
            <div className="bond-search-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search by power number, case number, or defendant name..."
                value={bondSearch}
                onChange={(e) => setBondSearch(e.target.value)}
                onFocus={() => bondResults.length > 0 && setShowBondDropdown(true)}
                className={errors.bond ? 'error' : ''}
              />
              {showBondDropdown && bondResults.length > 0 && (
                <div className="bond-search-dropdown">
                  {bondResults.map((bond) => (
                    <div
                      key={bond.id}
                      className="bond-result-item"
                      onClick={() => handleSelectBond(bond)}
                    >
                      <div className="bond-number">{bond.bond_number}</div>
                      <div className="bond-details">
                        {bond.defendant_name} • {bond.case_number || 'No Case #'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {errors.bond && <span className="error-message">{errors.bond}</span>}
            {selectedBond && (
              <div className="selected-bond-badge">
                Selected: <strong>{selectedBond.bond_number}</strong> - {selectedBond.defendant_name}
              </div>
            )}
          </div>

          {/* Court Selection */}
          <div className="form-row">
            <div className="form-group">
              <label>Select Court *</label>
              <select
                value={selectedCourt?.id || ''}
                onChange={handleCourtChange}
                className={errors.court ? 'error' : ''}
              >
                <option value="">Select court...</option>
                {courts.map((court) => (
                  <option key={court.id} value={court.id}>
                    {court.name}
                  </option>
                ))}
              </select>
              {errors.court && <span className="error-message">{errors.court}</span>}
            </div>

            <div className="form-group">
              <label>Court Jurisdiction</label>
              <input
                type="text"
                value={formData.courtJurisdiction}
                readOnly
                placeholder="Auto-filled from court"
                className="readonly-input"
              />
            </div>
          </div>

          {/* Date and Time */}
          <div className="form-row">
            <div className="form-group">
              <label>Court Date *</label>
              <input
                type="date"
                value={formData.courtDate}
                onChange={(e) => setFormData({...formData, courtDate: e.target.value})}
                className={errors.courtDate ? 'error' : ''}
              />
              {errors.courtDate && <span className="error-message">{errors.courtDate}</span>}
            </div>

            <div className="form-group">
              <label>Court Time</label>
              <input
                type="time"
                value={formData.courtTime}
                onChange={(e) => setFormData({...formData, courtTime: e.target.value})}
              />
            </div>
          </div>

          {/* Court Room and Hearing Type */}
          <div className="form-row">
            <div className="form-group">
              <label>Court Room</label>
              <input
                type="text"
                placeholder="Enter room number"
                value={formData.courtRoom}
                onChange={(e) => setFormData({...formData, courtRoom: e.target.value})}
              />
            </div>

            <div className="form-group">
              <label>Hearing Type *</label>
              <select
                value={formData.hearingType}
                onChange={(e) => setFormData({...formData, hearingType: e.target.value})}
                className={errors.hearingType ? 'error' : ''}
              >
                {HEARING_TYPES.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              {errors.hearingType && <span className="error-message">{errors.hearingType}</span>}
            </div>
          </div>

          {/* Case Number and Judge */}
          <div className="form-row">
            <div className="form-group">
              <label>Case Number</label>
              <input
                type="text"
                placeholder="Case number"
                value={formData.caseNumber}
                onChange={(e) => setFormData({...formData, caseNumber: e.target.value})}
              />
            </div>

            <div className="form-group">
              <label>Judge</label>
              <div className="judge-select-wrapper">
                <select
                  value={selectedJudge}
                  onChange={handleJudgeChange}
                  disabled={!selectedCourt}
                >
                  <option value="">Select judge...</option>
                  {judges.map((judge) => (
                    <option key={judge.id} value={judge.id}>{judge.name}</option>
                  ))}
                  <option value="__add_new__">+ Add New Judge</option>
                </select>
              </div>
            </div>
          </div>

          {/* Add New Judge */}
          {showAddJudge && (
            <div className="add-judge-section">
              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <input
                    type="text"
                    placeholder="Enter judge name"
                    value={newJudgeName}
                    onChange={(e) => setNewJudgeName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddJudge()}
                  />
                </div>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={handleAddJudge}
                  disabled={!newJudgeName.trim()}
                >
                  <FaPlus /> Add Judge
                </button>
              </div>
            </div>
          )}

          {/* Court Appearance */}
          <div className="form-group">
            <label>Court Appearance</label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="appearanceType"
                  value="In Person"
                  checked={formData.appearanceType === 'In Person'}
                  onChange={(e) => setFormData({...formData, appearanceType: e.target.value})}
                />
                In Person
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="appearanceType"
                  value="Virtual"
                  checked={formData.appearanceType === 'Virtual'}
                  onChange={(e) => setFormData({...formData, appearanceType: e.target.value})}
                />
                Virtual
              </label>
            </div>
          </div>

          {/* Comments */}
          <div className="form-group">
            <label>Comments</label>
            <textarea
              placeholder="Enter comments related to this court date"
              rows="3"
              value={formData.comments}
              onChange={(e) => setFormData({...formData, comments: e.target.value})}
            ></textarea>
          </div>

          {/* Reminders Toggle */}
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.setReminders}
                onChange={(e) => setFormData({...formData, setReminders: e.target.checked})}
              />
              Set Court Reminders
            </label>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'SAVE'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddCourtDateModal;
